import React, { useState } from 'react';
import { TagIcon, LightbulbIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { CTABand } from '../components/CTABand';
import { site } from '../data/site';
type Category = 'Special Offers' | 'Tips';
const categories: {
  name: Category;
  icon: typeof TagIcon;
  blurb: string;
}[] = [{
  name: 'Special Offers',
  icon: TagIcon,
  blurb: 'Seasonal offers are posted here when they are running. Call the estimate line for anything currently available.'
}, {
  name: 'Tips',
  icon: LightbulbIcon,
  blurb: 'Practical maintenance and project notes for Michigan homeowners will be published in this section.'
}];
const archivePosts: {
  title: string;
  category: Category;
}[] = [{
  title: 'Gutter Cleaning Special',
  category: 'Special Offers'
}];
export function Specials() {
  const [active, setActive] = useState<Category>('Special Offers');
  const posts = archivePosts.filter(p => p.category === active);
  return <>
      <PageHero eyebrow="Specials & Tips" title="Offers and homeowner advice" description="This section holds our special offers and maintenance tips. Details for any current offer come from the estimate line." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Specials & Tips'
    }]} data-id="element-533" data-mp="23btz97o77pkg" />

      <section className="mx-auto max-w-5xl px-6 py-12 md:py-16" data-id="element-534" data-mp="2hcemcg15c7jr">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-535" data-mp="2h9scg9qs9fyd">
          {categories.map(c => <button key={c.name} type="button" onClick={() => setActive(c.name)} aria-pressed={active === c.name} className={`rounded-sm border-2 bg-white p-6 text-left transition ${active === c.name ? 'border-safety shadow-card' : 'border-steel-200 hover:border-ink'}`} data-id="element-536" data-mp="2m8lkvnr7qgro">
              <c.icon className="h-6 w-6 text-safety" aria-hidden="true" data-id="element-537" data-mp="14yoib19sg5pq" />
              <h2 className="mt-3 font-display text-2xl font-semibold uppercase tracking-tight text-ink" data-id="element-538" data-mp="14wljpsukl49e">
                {c.name}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-steel-500" data-id="element-539" data-mp="1epsy237afulm">
                {c.blurb}
              </p>
            </button>)}
        </div>

        <div className="mt-10" data-id="element-540" data-mp="1npfo5sukhdzd">
          <SectionHeading eyebrow="Archive" title={active} data-id="element-541" data-mp="1noed3odjwrhn" />
          {posts.length === 0 ? <div className="mt-5 rounded-sm border border-dashed border-steel-300 bg-white p-10 text-center" data-id="element-542" data-mp="1jom45ak8wjbr">
              <p className="font-display text-xl font-semibold uppercase tracking-tight text-ink" data-id="element-543" data-mp="1xmamaamxhcd4">
                Nothing posted in this section yet
              </p>
              <p className="mx-auto mt-2 max-w-md text-sm text-steel-500" data-id="element-544" data-mp="39chu7nwt9xp">
                Tips will be added here. In the meantime, call and ask — we are
                happy to talk through a project before you commit to it.
              </p>
            </div> : <ul className="mt-5 divide-y divide-steel-200 overflow-hidden rounded-sm border border-steel-200 bg-white" data-id="element-545" data-mp="2a07djjvxn77s">
              {posts.map(p => <li key={p.title} className="flex flex-wrap items-center justify-between gap-3 px-5 py-5" data-id="element-546" data-mp="207a0ht9pmagk">
                  <div data-id="element-547" data-mp="255jwu7767h3r">
                    <h3 className="font-display text-xl font-semibold uppercase tracking-tight text-ink" data-id="element-548" data-mp="254ryiom6v59s">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-steel-400" data-id="element-549" data-mp="1w77vpb8oh0x2">
                      {p.category} · Archived post
                    </p>
                  </div>
                  <a href={site.estimatePhone.href} className="inline-flex items-center gap-2 rounded-sm border-2 border-ink px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-550" data-mp="1m91wx2i64awf">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-551" data-mp="1gj0j0vjaivau" />
                    Ask about it
                  </a>
                </li>)}
            </ul>}
          <p className="mt-4 text-sm text-steel-500" data-id="element-552" data-mp="17ikinuxug4a7">
            Offer terms are not published here. To find out what is currently
            available, call{' '}
            <a href={site.estimatePhone.href} className="font-semibold text-safety hover:underline" data-id="element-553" data-mp="xl65q5b4pxmn">
              {site.estimatePhone.display}
            </a>
            .
          </p>
        </div>
      </section>

      <CTABand data-id="element-554" data-mp="otdx805o0ykp" />
    </>;
}